import { Theme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Edge, Node } from "@xyflow/react";
import {
  DetailedCityEscape,
  DetailedProps,
  Scene,
} from "../../directus/collections";
import { isSceneTargetOfMultipleExits } from "../../utils/cityEscape";
import {
  CityEscapeElementNodeData,
  SceneExitNodeData,
  SceneNodeData,
} from "./types";

export const useCityEscapeFlow = (
  cityEscape: DetailedCityEscape,
  {
    theme,
    direction,
  }: {
    theme: Theme;
    direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
  }
) => {
  return useQuery({
    queryKey: ["cityEscapeFlowData", cityEscape.id, direction],
    queryFn: async () => {
      let { nodes, edges } = convertCityEscapeToFlowData(cityEscape, {
        theme,
      });
      // nodes = await runLayout(nodes, edges, { direction });
      return { nodes, edges };
    },
    enabled: !!cityEscape,
  });
};

// Helper function to convert City Escape data to ReactFlow format
export const convertCityEscapeToFlowData = (
  cityEscape: DetailedCityEscape,
  {
    expandedScenes = new Set<string>(),
  }: { theme: Theme; expandedScenes?: Set<string> }
): { nodes: Node<CityEscapeElementNodeData>[]; edges: Edge[] } => {
  const nodes: Node<CityEscapeElementNodeData>[] = [];
  const edges: Edge[] = [];

  // Safety check
  if (!cityEscape) {
    console.warn("CityEscape is null or undefined");
    return { nodes, edges };
  }

  const allScenes = [
    ...cityEscape.scenes,
    cityEscape.prologue,
    cityEscape.epilogue,
  ].filter(Boolean) as Scene[];

  // const startingScene = cityEscape.scenes.find((scene) => scene.is_start);
  const allOutputsTargets = new Set<string>(
    cityEscape.scenes.flatMap(
      (scene) =>
        scene.output_scenes_exits
          ?.flatMap((exit) => [
            exit?.target_scene || "",
            exit?.alternative_scene || "",
          ])
          .filter(Boolean) || []
    )
  );
  const originScenes = cityEscape.scenes.filter(
    (scene) => !allOutputsTargets.has(scene.id)
  );

  // Collect all alternative paths
  const alternativePaths = originScenes
    .map((startingScene) =>
      collectAlternativePaths(startingScene, allScenes, [])
    )
    .reduce((acc, set) => {
      {
        for (const path of set) acc.add(path);
        return acc;
      }
    }, new Set<string>());

  // Add prologue node
  if (cityEscape.prologue && cityEscape.prologue.id) {
    const prologueResult = processPrologue(cityEscape.prologue, {
      alternativePaths,
    });
    nodes.push(...prologueResult.nodes);
    edges.push(...prologueResult.edges);
  }

  // Add main scenes
  for (const scene of cityEscape.scenes) {
    if (scene && scene.id) {
      const sceneResult = processScene(scene, {
        alternativePaths,
        isExpanded: expandedScenes.has(String(scene.id)),
        props: cityEscape.props,
      });
      nodes.push(...sceneResult.nodes);
      edges.push(...sceneResult.edges);
    }
  }

  // Add epilogue node
  if (cityEscape.epilogue && cityEscape.epilogue.id) {
    const epilogueResult = processEpilogue(cityEscape.epilogue);
    nodes.push(...epilogueResult.nodes);
    edges.push(...epilogueResult.edges);
  }

  return { nodes, edges };
};

// Helper to get scene name from translations
const getSceneName = (scene: Scene): string => {
  return scene.translations?.[0]?.name || `Scena ${scene.id}`;
};

// Helper to generate safe ID
const getSafeId = (id: any): string => {
  return id ? String(id) : `scene_${Math.random().toString(36).substr(2, 9)}`;
};

const processScene = (
  scene: Scene,
  {
    alternativePaths = new Set<string>(),
    isExpanded = false,
    props = [],
  }: {
    alternativePaths?: Set<string>;
    isExpanded?: boolean;
    props?: DetailedProps[];
  }
) => {
  const nodes: Node<CityEscapeElementNodeData>[] = [];
  const edges: Edge[] = [];
  const sceneId = getSafeId(scene.id);
  const sceneName = getSceneName(scene);
  const stepCount = scene.steps?.length || 0;

  let sceneNode: Node<SceneNodeData> = {
    id: sceneId,
    type: "scene",
    position: { x: 0, y: 0 }, // ELK will calculate all positions
    data: {
      label: sceneName,
      subtitle: `${stepCount} steps`,
      nodeType: "scene",
      selected: false, // Will be set by parent component
      scene: scene,
      stepCount: stepCount,
      isExpanded: false, // Will be set by parent component
      isExpandable: stepCount > 0,
      tooltip: `<strong>Scena</strong><br/>${sceneName}<br/>${stepCount} steps`,
      activeLang: "it-IT",
      props: props,
    },
    style: {
      width: "auto",
      height: "auto",
    },
  };

  // RIUSO LOGICA ESISTENTE: Crea nodo scena con dimensioni calcolate

  nodes.push(sceneNode);
  // Create exit nodes for this scene
  const { nodes: exitNodes, edges: exitEdges } = createExitNodes(
    scene,
    sceneId,
    alternativePaths
  );
  nodes.push(...exitNodes);
  edges.push(...exitEdges);
  return { nodes, edges };
};

const collectAlternativePaths = (
  scene: Scene,
  allScenes: Scene[],
  currentPath: string[] = []
) => {
  const alternativePaths = new Set<string>();
  if (currentPath.length && isSceneTargetOfMultipleExits(scene.id, allScenes)) {
    alternativePaths.add([...currentPath, scene.id].join("->"));
    currentPath = [];
  }

  function deepDiveScene(sceneId: string, path: string[]) {
    const targetScene = allScenes.find((s) => s && s.id === sceneId);
    if (targetScene) {
      const paths = collectAlternativePaths(targetScene, allScenes, path);
      return paths;
    }
    return new Set<string>();
  }

  for (const exit of scene.output_scenes_exits || []) {
    if (exit.alternative_scene) {
      const scenePath = currentPath.length
        ? [...currentPath, scene.id]
        : [scene.id];
      const altSceneResult = deepDiveScene(exit.alternative_scene, scenePath);
      if (altSceneResult.size === 0) {
        alternativePaths.add([...scenePath, exit.alternative_scene].join("->"));
      } else {
        altSceneResult.forEach((p) => alternativePaths.add(p));
      }
    }

    if (exit.target_scene) {
      const scenePath = currentPath.length ? [...currentPath, scene.id] : [];
      const targetSceneResult = deepDiveScene(exit.target_scene, scenePath);
      targetSceneResult.forEach((p) => alternativePaths.add(p));
    }
  }

  return alternativePaths;
};

const processPrologue = (
  prologue: Scene,
  { alternativePaths }: { alternativePaths: Set<string> }
) => {
  const nodes: Node<CityEscapeElementNodeData>[] = [];
  const edges: Edge[] = [];
  const prologueId = getSafeId(prologue.id);
  const sceneName = getSceneName(prologue);
  const stepCount = prologue.steps?.length || 0;

  // RIUSO LOGICA ESISTENTE: Crea nodo prologo con dimensioni
  nodes.push({
    id: prologueId,
    type: "prologue",
    position: { x: 0, y: 0 }, // ELK will calculate all positions
    data: {
      label: sceneName,
      subtitle: `${stepCount} steps`,
      nodeType: "prologue",
      selected: false,
      scene: prologue,
      stepCount: stepCount,
      isExpanded: false,
      isExpandable: stepCount > 0,
      tooltip: `<strong>Prologo</strong><br/>${sceneName}<br/>${stepCount} steps`,
      activeLang: "it-IT",
    },
    // style: {
    //   width: 200,
    //   height: 70,
    // },
    style: {
      width: "auto",
      height: "auto",
    },
  });

  // Create exit nodes for prologue
  const result = createExitNodes(prologue, prologueId, alternativePaths);
  nodes.push(...result.nodes);
  edges.push(...result.edges);
  return { nodes, edges };
};

const processEpilogue = (epilogue: Scene) => {
  const nodes: Node<CityEscapeElementNodeData>[] = [];
  const edges: Edge[] = [];
  const epilogueId = getSafeId(epilogue.id);
  const sceneName = getSceneName(epilogue);
  const stepCount = epilogue.steps?.length || 0;

  // RIUSO LOGICA ESISTENTE: Crea nodo epilogo con dimensioni
  nodes.push({
    id: epilogueId,
    type: "epilogue",
    position: { x: 0, y: 0 }, // ELK will calculate all positions
    data: {
      label: sceneName,
      subtitle: `${stepCount} steps`,
      nodeType: "epilogue",
      selected: false,
      scene: epilogue,
      stepCount: stepCount,
      isExpanded: false,
      isExpandable: stepCount > 0,
      tooltip: `<strong>Epilogo</strong><br/>${sceneName}<br/>${stepCount} steps`,
      activeLang: "it-IT",
    },
    style: {
      width: "auto",
      height: "auto",
    },
  });

  return { nodes, edges };
};

const createEdge = (
  source: string,
  target: string,

  {
    type = "default",
    label = "",
    data = {},
    theme = undefined,
    isAlternative = false,
    animated = false,
  }: {
    label?: string;
    type?: string;
    data?: any;
    theme?: Theme;
    isAlternative?: boolean;
    animated?: boolean;
  }
): Edge => ({
  id: `${source}-${target}`,
  source,
  target,
  label,
  type,
  animated,
  style: {
    strokeWidth: 2,
    strokeDasharray: isAlternative ? "5,5" : undefined,
    stroke: theme?.palette.primary.main,
    fill: theme?.palette.text.secondary,
  },
  labelStyle: {
    fontSize: "10px",
    fontWeight: "normal",
    fill: theme?.palette.text.secondary,
  },
  labelBgStyle: {
    fill: theme?.palette.background.paper,
    fillOpacity: 0.8,
  },
  data: {
    isAlternative,
    ...data,
  },
});

// Helper to create exit nodes
const createExitNodes = (
  scene: Scene,
  sceneId: string,
  alternativePaths: Set<string>
) => {
  const nodes: Node<CityEscapeElementNodeData>[] = [];
  const edges: Edge[] = [];

  scene.output_scenes_exits?.forEach((exit, index) => {
    const isAlternative = Array.from(alternativePaths).some((p) =>
      p.includes(`${scene.id}->${exit.target_scene}`)
    );
    const exitId = `${sceneId}_exit_${index}`;
    if (exit.target_scene) {
      // RIUSO LOGICA ESISTENTE: Create exit node con dimensioni
      nodes.push({
        id: exitId,
        type: "exit",
        position: { x: 0, y: 0 }, // ELK will calculate all positions
        data: {
          label: exit.translations?.[0]?.label || `Uscita ${index + 1}`,
          subtitle: exit.alternative_scene ? "Con alternativa" : "Standard",
          nodeType: "exit",
          selected: false,
          exit: exit,
          isExpandable: false,
          tooltip: `<strong>Uscita</strong><br/>${
            exit.translations?.[0]?.label || "Uscita"
          }`,
          activeLang: "it-IT",
        } as SceneExitNodeData,
        style: {
          width: 100,
          height: 50,
        },
      });

      // Edge from scene to exit (dashed if scene is alternative, solid otherwise)
      edges.push(
        createEdge(sceneId, exitId, {
          isAlternative,
        })
      );

      // Edge from exit to target_scene (dashed if scene is alternative, solid otherwise)
      const targetId = getSafeId(exit.target_scene);
      edges.push(
        createEdge(exitId, targetId, {
          isAlternative,
        })
      );
    }

    // Edge from exit to alternative_scene (always dashed)
    if (exit.alternative_scene) {
      const alternativeId = getSafeId(exit.alternative_scene);
      edges.push(
        createEdge(exitId, alternativeId, {
          isAlternative: true,
        })
      );
    }
  });

  return { nodes, edges };
};
